/**
 * Horizontal review scroller.
 *
 * Arrows move one card at a time. The edge fades are driven from here rather
 * than left on permanently: a fade on a side with nothing beyond it reads as a
 * rendering fault, so `data-fade` reports which edges actually have more to
 * scroll to and the stylesheet masks only those.
 */
class ReviewCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-viewport]');
    if (!this.viewport) return;

    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.viewport.addEventListener('scroll', () => this.update(), { passive: true });

    // Card widths are fluid, so the reachable scroll range changes with layout. The observer
    // also fires once as soon as it starts observing, after layout, so there's no separate first
    // update(): calling it here read scroll sizes mid-load and forced a synchronous layout.
    this.observer = new ResizeObserver(() => this.update());
    this.observer.observe(this.viewport);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /** One card plus the gap either side of it. */
  get distance() {
    const card = this.viewport.querySelector('.review-card');
    if (!card) return this.viewport.clientWidth;
    const gap = parseFloat(getComputedStyle(this.viewport).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  step(direction) {
    this.viewport.scrollBy({ left: direction * this.distance, behavior: 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
    const max = scrollWidth - clientWidth;

    // Sub-pixel rounding means these never land exactly on 0 or max.
    const overflowing = max > 1;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft >= max - 1;

    if (this.prev) this.prev.disabled = !overflowing || atStart;
    if (this.next) this.next.disabled = !overflowing || atEnd;

    if (!overflowing) this.dataset.fade = 'none';
    else if (atStart) this.dataset.fade = 'end';
    else if (atEnd) this.dataset.fade = 'start';
    else this.dataset.fade = 'both';
  }
}

if (!customElements.get('review-carousel')) {
  customElements.define('review-carousel', ReviewCarousel);
}
